import React, { useRef, useEffect, useCallback } from 'react'
import WordBlockEditor from './WordBlockEditor'

/**
 * WordLevelTimelineTrack Component
 * Editable timeline track showing word-level transcription segments
 * Syncs scroll position with the main line-level track
 */
const WordLevelTimelineTrack = ({
  wordLevelTranscription,
  duration,
  currentTime,
  zoom,
  snapToGrid = false,
  timeToPixels,
  pixelsToTime,
  scrollLeft = 0,
  onScrollChange,
  selectedWordKey = null,
  onSelectWord,
  onUpdateWord,
  onEditWord,
  snapTime
}) => {
  const scrollContainerRef = useRef(null)
  const trackRef = useRef(null)
  const isExternalScrollRef = useRef(false)

  // Calculate total width based on duration and zoom
  const totalWidth = timeToPixels(duration, zoom) + 100 // Add padding

  // Sync scroll position from parent (line-level track)
  useEffect(() => {
    if (scrollContainerRef.current && scrollLeft !== undefined) {
      isExternalScrollRef.current = true
      scrollContainerRef.current.scrollLeft = scrollLeft
      // Reset flag after a short delay
      setTimeout(() => {
        isExternalScrollRef.current = false
      }, 50)
    }
  }, [scrollLeft])

  // Handle scroll - notify parent to sync tracks
  const handleScroll = (e) => {
    if (!isExternalScrollRef.current && onScrollChange) {
      onScrollChange(e.target.scrollLeft)
    }
  }

  // Flatten word-level transcription into individual word segments with keys
  const wordSegments = React.useMemo(() => {
    if (!wordLevelTranscription || !Array.isArray(wordLevelTranscription)) {
      return []
    }

    const segments = []
    wordLevelTranscription.forEach((line, lineIndex) => {
      if (line.segments && Array.isArray(line.segments)) {
        line.segments.forEach((word, wordIndex) => {
          if (word.start_time !== undefined && word.end_time !== undefined) {
            segments.push({
              ...word,
              lineIndex,
              wordIndex,
              key: `${lineIndex}-${wordIndex}`
            })
          }
        })
      }
    })
    return segments
  }, [wordLevelTranscription])

  // Find current word index based on currentTime
  const currentWordKey = React.useMemo(() => {
    for (const word of wordSegments) {
      if (currentTime >= word.start_time && currentTime < word.end_time) {
        return word.key
      }
    }
    return null
  }, [wordSegments, currentTime])

  // Determine if a word is past based on currentTime
  const isWordPast = useCallback((word) => {
    return currentTime >= word.end_time
  }, [currentTime])

  // Handle word selection
  const handleSelectWord = useCallback((wordKey) => {
    if (onSelectWord) {
      onSelectWord(wordKey)
    }
  }, [onSelectWord])

  // Handle word drag
  const handleDragWord = useCallback((wordKey, newTimes) => {
    if (onUpdateWord) {
      onUpdateWord(wordKey, newTimes)
    }
  }, [onUpdateWord])

  // Handle word resize
  const handleResizeWord = useCallback((wordKey, newTimes) => {
    if (onUpdateWord) {
      onUpdateWord(wordKey, newTimes)
    }
  }, [onUpdateWord])

  // Handle double-click to edit word text
  const handleEditWord = useCallback((wordKey) => {
    if (onEditWord) {
      onEditWord(wordKey)
    }
  }, [onEditWord])

  // Click on empty space to deselect
  const handleTrackClick = (e) => {
    if (e.target === trackRef.current || e.target === scrollContainerRef.current) {
      if (onSelectWord) {
        onSelectWord(null)
      }
    }
  }

  if (!wordLevelTranscription || wordLevelTranscription.length === 0) {
    return (
      <div className="relative">
        <div className="absolute left-0 top-0 z-10 bg-black/60 text-white/60 text-xs px-2 py-1 rounded-br-lg">
          Word-Level
        </div>
        <div className="relative h-16 bg-black/20 border-t border-white/10">
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white/40 text-sm">No word-level transcription available</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Track Label */}
      <div className="absolute left-0 top-0 z-10 bg-black/60 text-white/60 text-xs px-2 py-1 rounded-br-lg">
        Word-Level
      </div>

      <div
        ref={scrollContainerRef}
        className="relative h-16 overflow-x-auto overflow-y-hidden bg-black/20 border-t border-white/10 max-w-full min-w-0"
        onScroll={handleScroll}
        onClick={handleTrackClick}
      >
        <div
          ref={trackRef}
          className="relative h-full"
          style={{
            width: `${totalWidth}px`,
            backgroundImage: snapToGrid
              ? `repeating-linear-gradient(
                  to right,
                  rgba(255, 255, 255, 0.03) 0px,
                  rgba(255, 255, 255, 0.03) 1px,
                  transparent 1px,
                  transparent ${timeToPixels(1, zoom)}px
                )`
              : `repeating-linear-gradient(
                  to right,
                  rgba(255, 255, 255, 0.02) 0px,
                  rgba(255, 255, 255, 0.02) 1px,
                  transparent 1px,
                  transparent ${timeToPixels(1, zoom)}px
                )`
          }}
        >
          {/* Render word segments */}
          {wordSegments.map((word) => (
            <WordBlockEditor
              key={word.key}
              word={word}
              wordKey={word.key}
              isSelected={selectedWordKey === word.key}
              isCurrent={currentWordKey === word.key}
              isPast={isWordPast(word)}
              timeToPixels={timeToPixels}
              pixelsToTime={pixelsToTime}
              zoom={zoom}
              onSelect={handleSelectWord}
              onDoubleClick={handleEditWord}
              onDrag={handleDragWord}
              onResize={handleResizeWord}
              snapTime={snapTime}
            />
          ))}

          {/* Playhead indicator */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-purple-400/60 pointer-events-none z-30"
            style={{
              left: `${timeToPixels(currentTime, zoom)}px`,
              transform: 'translateX(-50%)'
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default WordLevelTimelineTrack
